import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Edit, FileText, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { purchaseOrderService } from '@/services/purchaseOrderService'

export const InventoryPurchaseOrdersList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const [rows, setRows] = useState<
    Array<{ id: number; code: string; reference: string; warehouse: string; orderDate: string; deliveryDate: string; total: number }>
  >([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await purchaseOrderService.getAll(branchId, pageSize, pageNumber)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch (error: any) {
        setRows([])
        setTotalCount(0)
        toast.error(error?.response?.data?.message || t('purchaseOrders.failedToLoadList'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, pageSize])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIdx = Math.min(pageNumber * pageSize, totalCount)

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    const locale = i18n.language?.startsWith('ar') ? 'ar-SA' : 'en-GB'
    return d.toLocaleDateString(locale)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseOrders.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('purchaseOrders.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={() => navigate('/inventory/purchase-orders/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('purchaseOrders.addOrder')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.code')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.reference')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.warehouse')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.orderDate')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.expectedDeliveryDate')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.total')}
                  </TableHead>
                  <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary">
                    {t('purchaseOrders.edit')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('purchaseOrders.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                      {t('purchaseOrders.noDataInTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="px-4 py-2 text-center">{row.code || '-'}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{row.reference || '-'}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{row.warehouse || '-'}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{fmtDate(row.orderDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{fmtDate(row.deliveryDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-center">{row.total.toFixed(2)}</TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/inventory/purchase-orders/${row.id}/edit`)}
                          className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {t('purchaseOrders.showingEntries', { from: startIdx, to: endIdx, total: totalCount })}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>
                {t('purchaseOrders.prev')}
              </Button>
              <span>
                {t('purchaseOrders.paginationPage', { current: pageNumber, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              >
                {t('purchaseOrders.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
